// Manage Subscription
//
// Everything about the account's plan, in our own UI. This used to be a link
// out to Stripe's hosted Billing Portal, which meant the one screen that tells
// a paying customer what they are paying for looked like someone else's
// product, and could not be changed.
//
// The two directions are not symmetrical, and the copy says so plainly:
//
//   Upgrade    takes effect immediately, prorated. They pay more to get more,
//              so making them wait for the renewal would be charging for
//              something they cannot use yet.
//   Downgrade  takes effect at the renewal date. Same principle the cancel
//              flow already follows: what they have paid for is theirs until
//              the period runs out. Nothing is refunded, so there is nothing
//              to explain afterwards.
//
// A scheduled downgrade is shown here and can be called off — otherwise a plan
// would quietly change under someone weeks after they clicked, with no trace
// in the app that they had asked for it.

import React from 'react';
import styled from 'styled-components';
import { Check, Crown, Clock, AlertCircle } from 'lucide-react';
import { theme } from '../../styles/theme';
import { billingService, SubscriptionTier } from '../../services/Billing/billingService';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider';
import {
  PLAN_TAGLINES,
  PREMIUM_FEATURES,
  PREMIUM_PLUS_FEATURES,
} from '../../config/planFeatures';
import { logger } from '../../utils/logger';
import { Icon } from '../shared';

interface ManageSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Hands off to the existing cancellation flow, which owns its own modal. */
  onCancelSubscription?: () => void;
  /** Undo a pending cancellation. */
  onReactivateSubscription?: () => void;
  /** No live subscription — send them to the pricing modal instead. */
  onSubscribe?: () => void;
}

interface PlanCopy {
  id: SubscriptionTier;
  name: string;
  price: string;
  tagline: string;
  features: string[];
}

// Prices are the regular rate, not the introductory one: this screen is for
// people who already subscribed, and the intro period is a first-purchase
// offer they have already used.
const PLANS: PlanCopy[] = [
  {
    id: 'premium',
    name: 'Premium',
    price: '$9.99',
    tagline: PLAN_TAGLINES.premium,
    features: PREMIUM_FEATURES,
  },
  {
    id: 'premium_plus',
    name: 'Premium Plus',
    price: '$19.99',
    tagline: PLAN_TAGLINES.premium_plus,
    features: PREMIUM_PLUS_FEATURES,
  },
];

const TIER_RANK: Record<SubscriptionTier, number> = { premium: 1, premium_plus: 2 };

const formatDate = (iso: string | null): string | null =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric',
    })
    : null;

const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: var(--bg-primary);
  backdrop-filter: blur(10px);
  z-index: 11000;
  display: ${props => (props.$isOpen ? 'flex' : 'none')};
  flex-direction: column;
  overflow-y: auto;
  padding: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
  }
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  color: var(--text-primary);
  padding: ${theme.spacing.xl} 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${theme.spacing.md};
  right: ${theme.spacing.md};
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: var(--bg-secondary);
  border: 1px solid var(--border-light);
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 300;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11001;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;

  &:hover {
    background: var(--bg-tertiary);
    transform: scale(1.1);
  }
`;

const Header = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: 1.9rem;
  font-weight: 700;
  margin-bottom: ${theme.spacing.xs};
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
`;

const CurrentPlanCard = styled.div`
  background: var(--bg-secondary);
  border: 2px solid #FFD700;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const CurrentPlanRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const CurrentPlanName = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: 1.4rem;
  font-weight: 700;
`;

const CurrentPlanPrice = styled.div`
  font-size: 1rem;
  color: var(--text-secondary);
`;

const StatusLine = styled.p<{ $tone?: 'normal' | 'warning' }>`
  margin-top: ${theme.spacing.sm};
  font-size: 0.95rem;
  color: ${props => (props.$tone === 'warning' ? theme.colors.warning : 'var(--text-secondary)')};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const PendingBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  background: var(--bg-tertiary);
  border: 1px solid var(--border-medium);
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  font-size: 0.95rem;
`;

const PendingText = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.md};
`;

const PlanCard = styled.div<{ $isCurrent: boolean }>`
  background: var(--bg-secondary);
  border: 1px solid ${props => (props.$isCurrent ? '#FFD700' : 'var(--border-light)')};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
  opacity: ${props => (props.$isCurrent ? 0.75 : 1)};
`;

const PlanHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.xs};
`;

const PlanName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
`;

const PlanPrice = styled.div`
  font-size: 1rem;
  color: var(--text-secondary);
`;

const PlanTagline = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin-bottom: ${theme.spacing.md};
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.xs};
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'quiet' }>`
  width: 100%;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    switch (props.$variant) {
      case 'secondary':
        return `
          background: var(--bg-tertiary);
          border: 1px solid var(--border-medium);
          color: var(--text-primary);
        `;
      case 'quiet':
        return `
          background: none;
          border: none;
          color: var(--text-secondary);
          font-weight: 500;
          text-decoration: underline;
        `;
      default:
        return `
          background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
          border: none;
          color: #1a1a1a;
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
`;

const EffectNote = styled.p`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-top: ${theme.spacing.xs};
  text-align: center;
`;

const Footer = styled.div`
  margin-top: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  align-items: center;
`;

const Message = styled.div<{ $tone: 'error' | 'success' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.95rem;
  background: var(--bg-secondary);
  border: 1px solid ${props => (props.$tone === 'error' ? theme.colors.error : theme.colors.success)};
  color: ${props => (props.$tone === 'error' ? theme.colors.error : 'var(--text-primary)')};
`;

export const ManageSubscriptionModal: React.FC<ManageSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onCancelSubscription,
  onReactivateSubscription,
  onSubscribe,
}) => {
  const { subscription, tier, isPremium, refreshAfterBillingChange } = useSubscriptionContext();
  const [pendingAction, setPendingAction] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const currentPlan = PLANS.find(plan => plan.id === tier) ?? null;
  const renewsOn = formatDate(subscription.currentPeriodEnd);
  const pendingPlan = PLANS.find(plan => plan.id === subscription.pendingTier) ?? null;
  const pendingOn = formatDate(subscription.pendingTierEffectiveAt);

  const handleChangePlan = async (plan: PlanCopy) => {
    if (pendingAction) return;
    setPendingAction(plan.id);
    setError(null);
    setSuccess(null);
    try {
      const result = await billingService.changePlan(plan.id);
      // The plan just moved, so every gate and badge in the app needs to see
      // it. refreshAfterBillingChange polls, because Stripe's webhook can
      // land after its own API call returns.
      refreshAfterBillingChange();
      const on = formatDate(result.effectiveAt);
      setSuccess(
        result.effective === 'now'
          ? `You're on ${plan.name} now.`
          : on
            ? `You'll move to ${plan.name} on ${on}. Nothing changes until then.`
            : `You'll move to ${plan.name} at the end of this billing period.`,
      );
    } catch (err: any) {
      logger.log('Plan change failed:', err);
      setError(err?.message || `Could not switch to ${plan.name}. Please try again.`);
    } finally {
      setPendingAction(null);
    }
  };

  const handleKeepCurrentPlan = async () => {
    if (pendingAction) return;
    setPendingAction('keep');
    setError(null);
    setSuccess(null);
    try {
      await billingService.cancelScheduledPlanChange();
      refreshAfterBillingChange();
      setSuccess(currentPlan ? `Staying on ${currentPlan.name}.` : 'Keeping your current plan.');
    } catch (err: any) {
      logger.log('Could not cancel the scheduled plan change:', err);
      setError(err?.message || 'Could not keep your current plan. Please try again.');
    } finally {
      setPendingAction(null);
    }
  };

  if (!isOpen) return null;

  return (
    <Backdrop $isOpen={isOpen}>
      <Content>
        <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>

        <Header>
          <Title>Your subscription</Title>
          <Subtitle>
            {isPremium
              ? 'See what you’re on, and change it whenever you like.'
              : 'You don’t have an active subscription right now.'}
          </Subtitle>
        </Header>

        {error && (
          <Message $tone="error">
            <Icon icon={AlertCircle} size={18} />
            {error}
          </Message>
        )}
        {success && (
          <Message $tone="success">
            <Icon icon={Check} size={18} />
            {success}
          </Message>
        )}

        {isPremium && currentPlan && (
          <CurrentPlanCard>
            <CurrentPlanRow>
              <CurrentPlanName>
                <Icon icon={Crown} size={22} />
                {currentPlan.name}
              </CurrentPlanName>
              <CurrentPlanPrice>{currentPlan.price}/month</CurrentPlanPrice>
            </CurrentPlanRow>

            {/* Ending, renewing, or in trouble — three different facts, and the
                user needs to be told which one applies to them. */}
            {subscription.cancelAtPeriodEnd ? (
              <StatusLine $tone="warning">
                <Icon icon={Clock} size={16} />
                {renewsOn ? `Ends on ${renewsOn}` : 'Ending at the end of this period'}
              </StatusLine>
            ) : subscription.status === 'past_due' ? (
              <StatusLine $tone="warning">
                <Icon icon={AlertCircle} size={16} />
                Payment failed — update your card to keep your plan
              </StatusLine>
            ) : (
              <StatusLine>
                <Icon icon={Clock} size={16} />
                {renewsOn ? `Renews on ${renewsOn}` : 'Active'}
              </StatusLine>
            )}
          </CurrentPlanCard>
        )}

        {pendingPlan && (
          <PendingBanner>
            <Icon icon={Clock} size={18} />
            <PendingText>
              <strong>
                {pendingOn
                  ? `Switching to ${pendingPlan.name} on ${pendingOn}.`
                  : `Switching to ${pendingPlan.name} at the end of this period.`}
              </strong>
              <div>You keep everything you have until then.</div>
              <ActionButton
                $variant="quiet"
                onClick={handleKeepCurrentPlan}
                disabled={pendingAction !== null}
                style={{ width: 'auto', padding: `${theme.spacing.xs} 0` }}
              >
                {pendingAction === 'keep'
                  ? 'Working…'
                  : `Keep ${currentPlan?.name ?? 'my current plan'} instead`}
              </ActionButton>
            </PendingText>
          </PendingBanner>
        )}

        <SectionTitle>{isPremium ? 'Change your plan' : 'Plans'}</SectionTitle>

        {PLANS.map(plan => {
          const isCurrent = isPremium && plan.id === tier;
          const isUpgrade =
            !tier || TIER_RANK[plan.id] > TIER_RANK[tier];
          const isPendingTarget = subscription.pendingTier === plan.id;

          return (
            <PlanCard key={plan.id} $isCurrent={isCurrent}>
              <PlanHead>
                <PlanName>{plan.name}</PlanName>
                <PlanPrice>{plan.price}/month</PlanPrice>
              </PlanHead>
              <PlanTagline>{plan.tagline}</PlanTagline>

              <FeatureList>
                {plan.features.map(feature => (
                  <FeatureItem key={feature}>
                    <Icon icon={Check} size={16} color={theme.colors.success} />
                    {feature}
                  </FeatureItem>
                ))}
              </FeatureList>

              {isCurrent ? (
                <ActionButton $variant="secondary" disabled>
                  Your current plan
                </ActionButton>
              ) : !isPremium ? (
                <ActionButton onClick={onSubscribe}>
                  Subscribe to {plan.name}
                </ActionButton>
              ) : isPendingTarget ? (
                <ActionButton $variant="secondary" disabled>
                  Scheduled{pendingOn ? ` for ${pendingOn}` : ''}
                </ActionButton>
              ) : (
                <>
                  <ActionButton
                    $variant={isUpgrade ? 'primary' : 'secondary'}
                    onClick={() => handleChangePlan(plan)}
                    disabled={pendingAction !== null}
                  >
                    {pendingAction === plan.id
                      ? 'Working…'
                      : isUpgrade
                        ? `Upgrade to ${plan.name}`
                        : `Switch to ${plan.name}`}
                  </ActionButton>
                  <EffectNote>
                    {isUpgrade
                      ? 'Starts right away. You’re charged the difference for the rest of this month.'
                      : renewsOn
                        ? `Starts on ${renewsOn}. You keep ${currentPlan?.name ?? 'your plan'} until then.`
                        : 'Starts at the end of your current billing period.'}
                  </EffectNote>
                </>
              )}
            </PlanCard>
          );
        })}

        <Footer>
          {subscription.canResume && (
            <ActionButton $variant="secondary" onClick={onReactivateSubscription}>
              Reactivate subscription
            </ActionButton>
          )}
          {subscription.canCancel && (
            <ActionButton $variant="quiet" onClick={onCancelSubscription}>
              Cancel subscription
            </ActionButton>
          )}
        </Footer>
      </Content>
    </Backdrop>
  );
};

export default ManageSubscriptionModal;
